/**
 * Shared numeric-series helpers for indicator calcs (talibIndicators,
 * maBandIndicators). All helpers treat `undefined` as "not yet warm":
 * outputs stay undefined until their window of consecutively defined
 * inputs is full.
 */

export type Num = number | undefined

/** Minimal bar shape indicator computes need — keeps tests klinecharts-free. */
export interface OhlcvBar {
  open: number
  high: number
  low: number
  close: number
  volume?: number
}

/** Rolling sum over the last `period` values; undefined until the window
 *  (of consecutively defined values) is full. */
export function rollingSum(values: readonly Num[], period: number): Num[] {
  const out: Num[] = new Array(values.length).fill(undefined)
  let sum = 0
  let run = 0
  for (let i = 0; i < values.length; i++) {
    const v = values[i]
    if (v == null || !Number.isFinite(v)) {
      sum = 0
      run = 0
      continue
    }
    sum += v
    run += 1
    if (run > period) {
      sum -= values[i - period] as number
      run = period
    }
    if (run >= period) out[i] = sum
  }
  return out
}

/** Rolling mean over the last `period` values; undefined until the window
 *  (of consecutively defined values) is full. */
export function rollingMean(values: readonly Num[], period: number): Num[] {
  const sums = rollingSum(values, period)
  return sums.map((s) => (s == null ? undefined : s / period))
}

/** Rolling sums drift in floating point over long series (klinecharts recalcs
 *  over up to ~50k bars per tick), so every RECOMPUTE_INTERVAL slides we
 *  recompute the window sums exactly — still amortized O(n). */
const RECOMPUTE_INTERVAL = 512

/** Linearly-weighted moving average (weights 1..period, newest heaviest).
 *  Single-pass O(n): keeps a plain sum S and weighted sum W over the window;
 *  sliding demotes every survivor by one weight (W −= S) and adds the newest
 *  at full weight (W += period·v). */
export function wmaSeries(values: readonly Num[], period: number): Num[] {
  const out: Num[] = new Array(values.length).fill(undefined)
  const denom = (period * (period + 1)) / 2
  let sum = 0 // plain sum over the current run (≤ period values)
  let wsum = 0 // weighted sum, oldest weight 1 … newest weight `run`
  let run = 0 // consecutive defined values ending at i
  let slides = 0
  for (let i = 0; i < values.length; i++) {
    const v = values[i]
    if (v == null || !Number.isFinite(v)) {
      sum = 0
      wsum = 0
      run = 0
      continue
    }
    if (run < period) {
      run += 1
      wsum += run * v
      sum += v
    } else {
      wsum = wsum - sum + period * v
      sum = sum - (values[i - period] as number) + v
      slides += 1
      if (slides >= RECOMPUTE_INTERVAL) {
        slides = 0
        sum = 0
        wsum = 0
        for (let j = 0; j < period; j++) {
          const w = values[i - period + 1 + j] as number
          sum += w
          wsum += (j + 1) * w
        }
      }
    }
    if (run >= period) out[i] = wsum / denom
  }
  return out
}

/** EMA seeded with the SMA of the first `period` defined values (TA-Lib style). */
export function emaSeries(values: readonly Num[], period: number): Num[] {
  const out: Num[] = new Array(values.length).fill(undefined)
  const k = 2 / (period + 1)
  let prev: number | undefined
  let seedSum = 0
  let seedCount = 0
  for (let i = 0; i < values.length; i++) {
    const v = values[i]
    if (v == null || !Number.isFinite(v)) continue
    if (prev === undefined) {
      seedSum += v
      seedCount += 1
      if (seedCount === period) {
        prev = seedSum / period
        out[i] = prev
      }
      continue
    }
    prev = prev + k * (v - prev)
    out[i] = prev
  }
  return out
}

export interface LinregFit {
  /** Slope per bar of the least-squares fit over the window. */
  slope: number
  /** Fitted value at the window's oldest bar (x = 0). */
  intercept: number
}

/**
 * Rolling ordinary-least-squares fit over the last `period` values, with
 * x = 0..period−1 (x = period−1 is the current bar — TA-Lib convention).
 * LINEARREG = intercept + slope·(period−1); TSF = intercept + slope·period.
 *
 * Single-pass O(n): the x-ramp is fixed, so sliding shifts every survivor's x
 * down by one (Σxy −= Σy − v_out; v_out carried x = 0) and adds the newest at
 * x = period−1. Sums are recomputed exactly every RECOMPUTE_INTERVAL slides
 * to cancel floating-point drift.
 */
export function linregSeries(values: readonly Num[], period: number): Array<LinregFit | undefined> {
  const out: Array<LinregFit | undefined> = new Array(values.length).fill(undefined)
  const sumX = ((period - 1) * period) / 2
  const sumX2 = ((period - 1) * period * (2 * period - 1)) / 6
  const denom = period * sumX2 - sumX * sumX
  if (denom === 0) return out // period 1 has no defined slope
  let sumY = 0 // Σ v over the current run (≤ period values)
  let sumXY = 0 // Σ x·v with x = 0 at the window's oldest bar
  let run = 0 // consecutive defined values ending at i
  let slides = 0
  for (let i = 0; i < values.length; i++) {
    const v = values[i]
    if (v == null || !Number.isFinite(v)) {
      sumY = 0
      sumXY = 0
      run = 0
      continue
    }
    if (run < period) {
      sumXY += run * v
      sumY += v
      run += 1
    } else {
      const vOut = values[i - period] as number
      sumXY = sumXY - (sumY - vOut) + (period - 1) * v
      sumY = sumY - vOut + v
      slides += 1
      if (slides >= RECOMPUTE_INTERVAL) {
        slides = 0
        sumY = 0
        sumXY = 0
        for (let x = 0; x < period; x++) {
          const w = values[i - period + 1 + x] as number
          sumY += w
          sumXY += x * w
        }
      }
    }
    if (run >= period) {
      const slope = (period * sumXY - sumX * sumY) / denom
      out[i] = { slope, intercept: (sumY - slope * sumX) / period }
    }
  }
  return out
}

/** Wilder RSI over a value series; defined from index `period` onward.
 *  Neutral 50 on a perfectly flat tape. */
export function rsiSeries(closes: readonly number[], period: number): Num[] {
  const gains: Num[] = new Array(closes.length).fill(undefined)
  const losses: Num[] = new Array(closes.length).fill(undefined)
  for (let i = 1; i < closes.length; i++) {
    const diff = closes[i] - closes[i - 1]
    gains[i] = diff > 0 ? diff : 0
    losses[i] = diff < 0 ? -diff : 0
  }
  const avgGain = wilderSeries(gains, period)
  const avgLoss = wilderSeries(losses, period)
  return closes.map((_, i) => {
    const g = avgGain[i]
    const l = avgLoss[i]
    if (g == null || l == null) return undefined
    if (l === 0) return g === 0 ? 50 : 100
    return 100 - 100 / (1 + g / l)
  })
}

/** Wilder-smoothed average, seeded with the SMA of the first `period` values. */
export function wilderSeries(values: readonly Num[], period: number): Num[] {
  const out: Num[] = new Array(values.length).fill(undefined)
  let prev: number | undefined
  let seedSum = 0
  let seedCount = 0
  for (let i = 0; i < values.length; i++) {
    const v = values[i]
    if (v == null || !Number.isFinite(v)) continue
    if (prev === undefined) {
      seedSum += v
      seedCount += 1
      if (seedCount === period) {
        prev = seedSum / period
        out[i] = prev
      }
      continue
    }
    prev = (prev * (period - 1) + v) / period
    out[i] = prev
  }
  return out
}

/**
 * True Range series: `max(high−low, |high−prevClose|, |low−prevClose|)`, with
 * the first bar falling back to `high−low` (no prior close). The shared base for
 * ATR/NATR (Wilder-smoothed) and Keltner Channels.
 */
export function trueRangeSeries(bars: readonly OhlcvBar[]): Num[] {
  return bars.map((b, i) => {
    if (i === 0) return b.high - b.low
    const prevClose = bars[i - 1].close
    return Math.max(b.high - b.low, Math.abs(b.high - prevClose), Math.abs(b.low - prevClose))
  })
}
